import {
  whitehavenWellnessStarterTemplate,
  type CmsInitialResourceProposal,
  type CmsInitialUserProposal,
  type CmsProvisioningDraft,
  type CmsProvisioningPlan,
} from "@whitehaven/contracts/cms-product";
import {
  canAccessPortalArea,
  type AuthorizationSubject,
} from "@whitehaven/permissions";
import {
  buildCmsProvisioningPlan,
  CmsProvisioningValidationError,
} from "@whitehaven/validation/cms-product";
import { ContentState } from "@whitehaven/ui";
import { useState, type FormEvent } from "react";

const emptyDraft: CmsProvisioningDraft = {
  templateId: whitehavenWellnessStarterTemplate.templateId,
  organizationId: "",
  siteId: "",
  siteName: whitehavenWellnessStarterTemplate.profileDefaults.siteName,
  organizationName:
    whitehavenWellnessStarterTemplate.profileDefaults.organizationName,
  legalName: whitehavenWellnessStarterTemplate.profileDefaults.legalName,
  primaryDomain: "",
  contactEmail: whitehavenWellnessStarterTemplate.profileDefaults.contactEmail,
  contactPhone: null,
  locality: whitehavenWellnessStarterTemplate.profileDefaults.locality,
  region: whitehavenWellnessStarterTemplate.profileDefaults.region,
  countryCode: whitehavenWellnessStarterTemplate.profileDefaults.countryCode,
  privacyPolicyPath:
    whitehavenWellnessStarterTemplate.profileDefaults.privacyPolicyPath,
  termsPath: whitehavenWellnessStarterTemplate.profileDefaults.termsPath,
  emergencyNotice:
    whitehavenWellnessStarterTemplate.profileDefaults.emergencyNotice,
  themePreset: whitehavenWellnessStarterTemplate.profileDefaults.theme,
  assets: [],
  initialUsers: [],
  initialResources: [],
};

function parsedUsers(value: string): readonly CmsInitialUserProposal[] {
  return value
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [email = "", roleKey = ""] = line.split("|");
      return { email: email.trim(), roleKey: roleKey.trim().toUpperCase() };
    });
}

function parsedResources(value: string): readonly CmsInitialResourceProposal[] {
  return value
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [kind = "service", name = "", description = ""] = line.split("|");
      const safeKind = ["service", "package", "room", "staff"].includes(kind)
        ? (kind as CmsInitialResourceProposal["kind"])
        : "service";
      return {
        kind: safeKind,
        name: name.trim(),
        description: description.trim(),
      };
    });
}

function downloadPlan(plan: CmsProvisioningPlan) {
  const url = URL.createObjectURL(
    new Blob([`${JSON.stringify(plan, null, 2)}\n`], {
      type: "application/json",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `${plan.profile.siteId}-provisioning-plan.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export function SiteProvisioningRoute({
  subject,
}: {
  readonly subject: AuthorizationSubject | null;
}) {
  const canView = Boolean(
    subject && canAccessPortalArea(subject, "admin-site-setup", "view"),
  );
  const canManage = Boolean(
    subject && canAccessPortalArea(subject, "admin-site-setup", "manage"),
  );
  const [draft, setDraft] = useState<CmsProvisioningDraft>(emptyDraft);
  const [users, setUsers] = useState("");
  const [resources, setResources] = useState("");
  const [logoSource, setLogoSource] = useState("");
  const [logoAlt, setLogoAlt] = useState("");
  const [plan, setPlan] = useState<CmsProvisioningPlan | null>(null);
  const [issues, setIssues] = useState<readonly string[]>([]);

  if (!canView) {
    return (
      <ContentState kind="error" title="Site setup access denied">
        <p>Your role cannot view the site-replication workflow.</p>
      </ContentState>
    );
  }

  const textField = (
    key: keyof Pick<
      CmsProvisioningDraft,
      | "organizationId"
      | "siteId"
      | "siteName"
      | "organizationName"
      | "legalName"
      | "primaryDomain"
      | "contactEmail"
      | "locality"
      | "region"
      | "countryCode"
      | "privacyPolicyPath"
      | "emergencyNotice"
    >,
    label: string,
  ) => (
    <label>
      <span>{label}</span>
      <input
        disabled={!canManage}
        required
        value={draft[key]}
        onChange={(event) =>
          setDraft({ ...draft, [key]: event.currentTarget.value })
        }
      />
    </label>
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canManage) return;
    try {
      const next = buildCmsProvisioningPlan({
        ...draft,
        assets:
          logoSource && logoAlt
            ? [
                {
                  id: "primary-logo",
                  kind: "logo",
                  source: logoSource,
                  altText: logoAlt,
                  width: 1200,
                  height: 400,
                },
              ]
            : [],
        initialUsers: parsedUsers(users),
        initialResources: parsedResources(resources),
      });
      setPlan(next);
      setIssues([]);
    } catch (error) {
      setPlan(null);
      setIssues(
        error instanceof CmsProvisioningValidationError
          ? error.issues
          : ["The setup plan could not be generated."],
      );
    }
  };

  return (
    <div className="site-provisioning">
      <header>
        <p className="portal-kicker">Reusable CMS</p>
        <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
          Replicate a wellness hub
        </h1>
        <p>
          Choose an approved starter, complete the site setup, and generate a
          validated dry-run. This screen never creates a tenant, sends an
          invite, changes DNS, provisions an external resource, or publishes
          content.
        </p>
      </header>
      {!canManage && (
        <div className="homepage-editor__read-only">
          Read-only access. The setup form and plan generation are disabled.
        </div>
      )}
      <form onSubmit={submit}>
        <fieldset disabled={!canManage}>
          <legend>1. Approved starter template</legend>
          <label>
            <span>Template</span>
            <select value={draft.templateId} disabled>
              <option value={whitehavenWellnessStarterTemplate.templateId}>
                {whitehavenWellnessStarterTemplate.label}
              </option>
            </select>
          </label>
          <p>{whitehavenWellnessStarterTemplate.description}</p>
        </fieldset>
        <fieldset disabled={!canManage}>
          <legend>2. Organization and site identity</legend>
          <div className="site-provisioning__grid">
            {textField("organizationId", "Organization ID")}
            {textField("siteId", "Site ID")}
            {textField("siteName", "Site name")}
            {textField("organizationName", "Organization name")}
            {textField("legalName", "Legal name")}
            {textField("primaryDomain", "Primary domain (no https://)")}
          </div>
        </fieldset>
        <fieldset disabled={!canManage}>
          <legend>3. Contact, legal, and brand</legend>
          <div className="site-provisioning__grid">
            {textField("contactEmail", "Public contact email")}
            {textField("locality", "City / locality")}
            {textField("region", "Region")}
            {textField("countryCode", "Country code")}
            {textField("privacyPolicyPath", "Privacy policy path")}
            {textField("emergencyNotice", "Legal / emergency notice")}
            <label>
              <span>Logo source (/media/ or HTTPS)</span>
              <input
                value={logoSource}
                onChange={(event) => setLogoSource(event.currentTarget.value)}
              />
            </label>
            <label>
              <span>Logo alternative text</span>
              <input
                value={logoAlt}
                onChange={(event) => setLogoAlt(event.currentTarget.value)}
              />
            </label>
          </div>
        </fieldset>
        <fieldset disabled={!canManage}>
          <legend>4. Approved theme preset</legend>
          <label>
            <span>Palette</span>
            <select
              value={draft.themePreset.palette}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  themePreset: {
                    ...draft.themePreset,
                    palette: event.currentTarget
                      .value as CmsProvisioningDraft["themePreset"]["palette"],
                  },
                })
              }
            >
              <option value="whitehaven">Whitehaven</option>
              <option value="ocean">Ocean</option>
              <option value="island">Island</option>
            </select>
          </label>
        </fieldset>
        <fieldset disabled={!canManage}>
          <legend>5. Proposed users and Resources</legend>
          <label>
            <span>Initial users — one per line: email|ROLE</span>
            <textarea
              rows={4}
              value={users}
              onChange={(event) => setUsers(event.currentTarget.value)}
            />
          </label>
          <label>
            <span>Initial Resources — one per line: kind|name|description</span>
            <textarea
              rows={4}
              value={resources}
              onChange={(event) => setResources(event.currentTarget.value)}
            />
          </label>
        </fieldset>
        <button type="submit" disabled={!canManage}>
          Validate dry-run setup plan
        </button>
      </form>
      {issues.length > 0 && (
        <div role="alert">
          <b>Setup needs attention</b>
          <ul>
            {issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </div>
      )}
      {plan && (
        <section aria-labelledby="setup-plan-title">
          <h2 id="setup-plan-title">Validated plan — no changes made</h2>
          <p>
            The cloned homepage, tenant profile, navigation, global blocks,
            redirects, proposed grants, and Resources are packaged together.
            {` ${plan.requiredContentReviewPaths.length} template-specific content paths require review before first publish.`}
          </p>
          <button type="button" onClick={() => downloadPlan(plan)}>
            Download portable setup plan
          </button>
        </section>
      )}
    </div>
  );
}
